'use strict'

import LABColor from './cxcolors/LABColor.js'
import RGBColor from './cxcolors/RGBColor.js'
import ColorsMath from './cxcolors/ColorsMath.js'
import dayjs from 'dayjs'
import SwatchPage from './models/SwatchPage'
import Swatch from './models/Swatch'
import Color from './models/Color'
import { DOM } from './ui/DOM.js'
import { Selector } from './ui/Selectors.js'
import SwatchDataCache from './ui/SwatchDataCache.js'

/**
 * Utils
 * Shared helpers for device detection, color workflows, and UI wiring.
 * Keep functions stateless and side-effect free where possible.
 */
class Utils {
    /**
     * Schedule non-critical work after the current interaction/rendering burst.
     *
     * `requestIdleCallback` is preferred when available so the browser can fit
     * the work around higher-priority tasks. The timeout keeps the task from
     * being postponed indefinitely.
     *
     * @param {() => void|Promise<void>} task
     * @param {number} [timeout=1000]
     * @returns {void}
     */
    static runWhenIdle(task, timeout = 1000) {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(() => void task(), { timeout })
            return
        }

        window.setTimeout(() => void task(), 0)
    }

    /**
     * Detect iOS devices by platform/user agent.
     * @param {Navigator} [navigatorRef=window.navigator]
     * @returns {boolean}
     */
    static checkiOSOnNavigator(navigatorRef = window.navigator) {
        const platform = navigatorRef?.platform || ''
        const ua = navigatorRef?.userAgent || ''
        const iDevices = ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod']

        return iDevices.includes(platform) || /iPad|iPhone|iPod/i.test(ua)
    }

    /**
     * Check whether the current device supports touch input.
     * @param {Navigator} navigator
     * @returns {boolean}
     */
    static isTouchScreenDevice(navigator) {
        const nav = navigator || window.navigator
        return 'ontouchstart' in window || (nav?.maxTouchPoints || 0) > 0 || (nav?.msMaxTouchPoints || 0) > 0
    }

    /**
     * Detect iPad devices (including iPadOS on Mac hardware).
     * @param {Navigator} navigator
     * @returns {boolean}
     */
    static isiPad(navigator) {
        const nav = navigator || window.navigator
        const platform = nav?.platform || ''
        const ua = nav?.userAgent || ''
        const isIpadPlatform = platform === 'iPad'
        const isIpadOS13 = platform === 'MacIntel' && Utils.isTouchScreenDevice(nav)
        const isIpadUA = /iPad/i.test(ua)

        return isIpadPlatform || isIpadOS13 || isIpadUA
    }

    /**
     * Detect mobile user agents.
     * @param {Navigator} navigator
     * @returns {boolean}
     */
    static isMobile(navigator) {
        const nav = navigator || window.navigator
        const ua = nav?.userAgent || ''
        return /iPhone|iPad|iPod|iOS|BlackBerry|Opera Mini|IEMobile|Android/i.test(ua)
    }

    /**
     * Detect iPhone devices by platform/user agent.
     * @param {Navigator} navigator
     * @returns {boolean}
     */
    static isiPhone(navigator) {
        const nav = navigator || window.navigator
        const platform = nav?.platform || ''
        const ua = nav?.userAgent || ''
        return /iP(hone|od)/.test(platform) || /iPhone/i.test(ua)
    }

    /**
     * Return iPhone OS version as [major, minor, patch].
     * @param {Navigator} navigator
     * @returns {number[]|undefined}
     */
    static getiPhoneVersion(navigator) {
        const nav = navigator || window.navigator
        const platform = nav?.platform || ''
        const match = nav?.appVersion?.match(/OS (\d+)_(\d+)_?(\d+)?/)

        if (!/iP(hone|od)/.test(platform) || !match) return undefined
        return [parseInt(match[1], 10), parseInt(match[2], 10), parseInt(match[3] || 0, 10)]
    }

    /**
     * Detect iPhone or iPad devices.
     * @param {Navigator} navigator
     * @returns {boolean}
     */
    static isiPhoneAndiPad(navigator) {
        return this.isiPhone(navigator) || this.isiPad(navigator)
    }

    /**
     * Hide Editing tools for all touchscreen, except iPad.
     * @param {Navigator} navigator
     * @returns {boolean}
     */
    static hideEditingTools(navigator) {
        return Utils.isTouchScreenDevice(navigator) && !Utils.isiPad(navigator)
    }

    /**
     * Return a short platform identifier for debugging.
     * @param {Navigator} [navigatorRef=window.navigator]
     * @returns {string}
     */
    static checkNavigatorName(navigatorRef = window.navigator) {
        return navigatorRef?.platform || navigatorRef?.userAgent || ''
    }

    /**
     * Check whether an object/array is empty.
     * @param {Object|Array} obj
     * @returns {boolean}
     */
    static isEmpty(obj) {
        return [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length
    }

    /**
     * Compare two arrays by value.
     * @param {Array} a
     * @param {Array} b
     * @returns {boolean}
     */
    static arraysEqual(a, b) {
        if (a === b) return true
        if (a == null || b == null) return false
        if (a.length != b.length) return false

        for (let i = 0; i < a.length; ++i) {
            if (a[i] !== b[i]) return false
        }
        return true
    }

    /**
     * Return swatch codes and quantities for the current color sample.
     * @param {Application} application
     * @returns {[string[], string[]]}
     */
    static getCurrentColorsCode(application) {
        const colorSample = application?.canvasState?.colorSample
        if (!colorSample || colorSample.numberOfColors() <= 0) {
            return [[], []]
        }

        const codes = []
        const qty = []
        const companyFolder = String(application?.companyAssetFolder || application?.subdomain || '').toLowerCase()
        const isColorix = companyFolder === 'colorix'

        for (let i = 1; i <= colorSample.numberOfColors(); i++) {
            const color = colorSample.getColorByNumber(i)
            if (!color?.swatch_color?.name) continue

            // Colorix order endpoint accepts Eurotrend only.
            if (isColorix && Number(color?.swatch?.id) !== 966) {
                continue
            }

            codes.push(color.swatch_color.name)
            qty.push('1')
        }

        const text = codes
        console.log(text)
        console.log(qty)
        return [codes, qty]
    }

    /**
     * Detect legacy iOS environments missing key APIs.
     * @param {Navigator} [navigatorRef=window.navigator]
     * @returns {boolean}
     */
    static checkiOSOnVersion(navigatorRef = window.navigator) {
        const ua = navigatorRef?.userAgent || ''
        const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream
        if (!isIOS) return false

        return (
            !window.indexedDB ||
            !window.SpeechSynthesisUtterance ||
            !window.webkitAudioContext ||
            !window.matchMedia ||
            !window.history ||
            !('pushState' in window.history)
        )
    }

    /**
     * Replace all occurrences of `find` with `replace`.
     * @param {string} str
     * @param {string|RegExp} find
     * @param {string} replace
     * @returns {string}
     */
    static replaceAll(str, find, replace) {
        return str.replace(new RegExp(find, 'g'), replace)
    }

    /**
     * Decode a base64 payload into unicode text.
     * @param {string} str
     * @returns {string}
     */
    static b64DecodeUnicode(str) {
        return decodeURIComponent(
            Array.prototype.map
                .call(atob(str), function (c) {
                    return '%' + c.charCodeAt(0).toString(16)
                })
                .join(''),
        )
    }

    /**
     * Compute luminosity
     * @param {number} A
     * @param {number} B
     * @param {number} grayVal
     * @param {number} CR
     * @param {number} CG
     * @param {number} CB
     * @returns {number[]}
     */
    static luminosity(A, B, grayVal, CR, CG, CB) {
        let result = []

        const norm = (B * grayVal) / (A + grayVal) / 255.0
        result[1] = ~~(norm * CR)
        result[2] = ~~(norm * CG)
        result[3] = ~~(norm * CB)

        return result
    }

    /**
     * Encode sRGB values into the app's swatch info payload.
     * @param {number} r
     * @param {number} g
     * @param {number} b
     * @returns {string}
     */
    static setSwatchColorInfos(r, g, b) {
        return btoa(
            encodeURIComponent('RGB#SRGB#' + r + '#' + g + '#' + b).replace(/%([0-9A-F]{2})/g, function (match, p1) {
                return String.fromCharCode('0x' + p1)
            }),
        )
    }

    /**
     * Normalize free-text for search (lowercase + trim).
     * @param {string} value
     * @returns {string}
     */
    static normalizeText(value) {
        return String(value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
    }

    /**
     * Return texture information from ID
     * @param {string|number} textureid Texture ID
     * @param {SwatchSelectionState} swatchSelection
     * @returns {Object}
     */
    static getTextureFromID(textureid, swatchSelection) {
        console.log('getTextureFromID')

        let swatch_page
        let swatch
        let result = {}

        for (let index = 0; index < swatchSelection.currentTextureSwatch.texture_pages.length; index++) {
            const page = swatchSelection.currentTextureSwatch.texture_pages[index]

            const color = page.textures.filter(function (value) {
                if (value.id === parseInt(textureid)) {
                    return value
                }
            })

            if (color.length > 0) {
                swatch_page = JSON.parse(JSON.stringify(page)) // store current swatch_page
                delete swatch_page.textures
                delete swatch_page.texture_swatch

                swatch = JSON.parse(JSON.stringify(swatchSelection.currentTextureSwatch))
                delete swatch.texture_pages // delete swatch_page

                result.texture = color[0]
                result.texture_page = swatch_page // replace swatch_page
                result.texture_swatch = swatch
                delete result.texture_pages // delete swatch_page

                break // Break loop if found
            }
        }

        return result
    }

    /**
     *
     * Resolve a swatch color by id and hydrate it into a Color model.
     * Uses cache when available, then falls back to scanning the current CSV.
     *
     * @param {Application} application
     * @param {number|string} swatchColorId
     * @param {number} dropcolor_position
     * @returns {Promise<Color>}
     */
    static getSwatchColorFromSwatchWithID(application, swatchColorId, dropcolor_position = 0) {
        console.log('getSwatchColorFromSwatchWithID:' + swatchColorId)

        let swatchColor
        let swatchPage
        let swatch
        const dataCache = SwatchDataCache.getInstance()
        const cached = dataCache.getSwatchColorMetaById(swatchColorId)

        if (cached) {
            swatchColor = cached.color
            swatchPage = cached.swatchPage
            swatch = dataCache.getSwatch(cached.swatchId)
        }

        if (!swatchColor) {
            // iterate over swatchPages (fallback when cache isn't ready)
            for (let index = 0; index < application.swatchSelection.currentSwatch.swatch_pages.length; index++) {
                const page = application.swatchSelection.currentSwatch.swatch_pages[index]

                // find swatchColor
                const _swatch_color = page.swatch_colors.filter(function (value) {
                    if (value.id === parseInt(swatchColorId)) {
                        return value
                    }
                })

                if (_swatch_color.length > 0) {
                    swatchPage = JSON.parse(JSON.stringify(page)) // store current swatchPage
                    swatch = JSON.parse(JSON.stringify(application.swatchSelection.currentSwatch))
                    swatchColor = _swatch_color[0]
                    break
                }
            }
        }

        const currentSwatchId = application?.swatchSelection?.currentSwatchId
        if (currentSwatchId == null) {
            console.warn('[Utils] Missing current swatch id in SwatchSelectionState.')
        }
        const color = new Promise((resolve) => {
            Color.create({
                swatch_color_data: swatchColor,
                dropcolor_position: dropcolor_position,
                current_swatch_id: currentSwatchId,
                listOfColorChartsServerID: application.listOfColorChartsServerID,
            }).then((result) => {
                if (swatchPage) result.swatch_page = new SwatchPage(JSON.parse(JSON.stringify(swatchPage)))
                if (swatch) result.swatch = new Swatch(JSON.parse(JSON.stringify(swatch)))
                resolve(result)
            })
        })

        return color
    }

    /**
     * Parse an encoded color info string into sRGB.
     * @param {string} infos
     * @returns {RGBColor}
     */
    static rgbFromInfos(infos) {
        console.log('rgbFromInfos')

        const rgb = this.b64DecodeUnicode(infos).split('#')

        let srgb

        switch (rgb[0]) {
            case 'RGB':
                srgb = new RGBColor(Number(rgb[2]), Number(rgb[3]), Number(rgb[4]))
                break

            case 'LAB': {
                const lab = new LABColor(Number(rgb[2]), Number(rgb[3]), Number(rgb[4]))
                srgb = ColorsMath.cxLABToSRGB_2D65(lab)
                break
            }
        }
        return srgb
    }

    /**
     * Convert a swatch color (sr/sg/sb) into an RGB tuple for UI usage.
     * @param {Object} swatchColor
     * @returns {{r:number,g:number,b:number}|null}
     */
    static swatchColorToRgb(swatchColor) {
        if (!swatchColor) return null

        const r = Number(swatchColor.sr)
        const g = Number(swatchColor.sg)
        const b = Number(swatchColor.sb)

        if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return null
        return { r: Math.round(r), g: Math.round(g), b: Math.round(b) }
    }

    /**
     * Convert RGB object to CSS rgb() string.
     * @param {{r:number,g:number,b:number}} rgb
     * @returns {string}
     */
    static rgbToCss(rgb) {
        return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`
    }

    /**
     * Map a touch event to canvas coordinates.
     * @param {HTMLCanvasElement} canvas
     * @param {TouchEvent} e
     * @returns {{x:number,y:number}}
     */
    static getTouchPos(canvas, e) {
        let rect = canvas.getBoundingClientRect() // abs. size of element
        let scaleX = canvas.width / rect.width // relationship bitmap vs. element for X
        let scaleY = canvas.height / rect.height // relationship bitmap vs. element for Y

        return {
            x: ~~((e.touches[0].clientX - rect.left) * scaleX), // scale mouse coordinates after they have
            y: ~~((e.touches[0].clientY - rect.top) * scaleY), // been adjusted to be relative to element
        }
    }

    /**
     * Map a mouse event to canvas coordinates.
     * @param {HTMLCanvasElement} canvas
     * @param {MouseEvent} e
     * @returns {{x:number,y:number}}
     */
    static getMousePos(canvas, e) {
        let rect = canvas.getBoundingClientRect() // abs. size of element
        let scaleX = canvas.width / rect.width // relationship bitmap vs. element for X
        let scaleY = canvas.height / rect.height // relationship bitmap vs. element for Y

        return {
            x: ~~((e.clientX - rect.left) * scaleX), // scale mouse coordinates after they have
            y: ~~((e.clientY - rect.top) * scaleY), // been adjusted to be relative to element
        }
    }

    /**
     * Compute drop coordinates on the main canvas from a draggable helper.
     * @param {CanvasState} canvasState
     * @param {{ helper: JQuery<HTMLElement>[] }} ui
     * @returns {[number, number]}
     */
    static getImageViewDroppableCoordinates(canvasState, ui) {
        // 1. Get the canvas DOM element
        const canvasEl = DOM.get(Selector.canvasMain)

        // 2. Get the canvas position and size relative to the viewport
        const canvasRect = canvasEl.getBoundingClientRect()

        // 3. Get the dragged element (helper) as a plain DOM element
        //    ui.helper may be a jQuery object, so we normalize it
        const helperEl = ui.helper instanceof HTMLElement ? ui.helper : ui.helper[0]

        // 4. Get the helper position and size relative to the viewport
        const helperRect = helperEl.getBoundingClientRect()

        // 5. Compute the position of the helper's CENTER relative to the canvas
        //    - helperRect.left / top: position in viewport
        //    - canvasRect.left / top: position of the canvas in viewport
        //    - helperRect.width / height / 2: shift from top-left to center
        const rawX = helperRect.left - canvasRect.left + helperRect.width / 2
        const rawY = helperRect.top - canvasRect.top + helperRect.height / 2

        // 6. Apply the canvas scaling ratio (from view to actual canvas coordinates)
        const ratio = canvasState._ratioCanvas || 1
        const xPos = Math.round(rawX * ratio)
        const yPos = Math.round(rawY * ratio)

        // 7. Return the final coordinates on the canvas
        return [xPos, yPos]
    }

    /**
     * Degree to radian
     *
     * @param {number} deg
     * @returns {number}
     */
    static degreeToRad(deg) {
        return (deg * Math.PI) / 180
    }

    /**
     * Load Texture image
     * @param {HTMLImageElement} img
     * @returns {string}
     */
    static getBase64Image(img) {
        // Create an empty canvas element
        let canvas = document.createElement('canvas')
        let ctx = canvas.getContext('2d')

        canvas.width = img.width
        canvas.height = img.height

        // Copy the image contents to the canvas
        ctx.drawImage(img, 0, 0)

        // Get the data-URL formatted image
        // Firefox supports PNG and JPEG. You could check img.src to
        // guess the original format, but be aware the using "image/jpg"
        // will re-encode the image.
        return canvas.toDataURL('image/png', 1.0)
    }

    /**
     * Generate a GUID-like identifier.
     * @returns {string}
     */
    static guid() {
        return this.s4() + this.s4() + '-' + this.s4() + '-' + this.s4() + '-' + this.s4() + '-' + this.s4() + this.s4() + this.s4()
    }

    /**
     * Generate a 4-hex-digit segment.
     * @returns {string}
     */
    static s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1)
    }

    /**
     * Build an export identifier.
     * @param {string} device
     * @param {string} company
     * @param {string} type
     * @returns {string}
     */
    static export_generate_uuid(device, company, type) {
        return device + '-' + company + '-' + this.guid() + '-' + type + '-' + new Date().getTime()
    }

    /**
     * Return the current timestamp in ISO-like format.
     * @returns {string}
     */
    static getCurrentTime() {
        return dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZZ')
    }

    /**
     * Helper for filtering app ids.
     * @param {Array} array
     * @returns {boolean}
     */
    static filterAppID(array) {
        if (this in array) {
            return true
        } else {
            return false
        }
    }

    /**
     * Recompute texture scale when an image is resized.
     * @param {number} oldScale
     * @param {number} old_imgWidth
     * @param {number} imgWidth
     * @returns {number}
     */
    static computeTextureScale(oldScale, old_imgWidth, imgWidth) {
        let ratio = old_imgWidth / imgWidth
        return oldScale * ratio
    }

    /**
     * Encode HTML entities for safe display.
     * @param {string} text
     * @returns {string}
     */
    static encodeHTML(text) {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;')
    }

    /**
     * Load an image from a given URL
     * @param {String} url The URL of the image resource
     * @returns {Promise<Image>} The loaded image
     */
    static loadImage(url) {
        /*
         * We are going to return a Promise which, when we .then
         * will give us an Image that should be fully loaded
         */
        return new Promise((resolve) => {
            /*
             * Create the image that we are going to use to
             * to hold the resource
             */
            const image = new Image()
            /*
             * The Image API deals in even listeners and callbacks
             * we attach a listener for the "load" event which fires
             * when the Image has finished the network request and
             * populated the Image with data
             */
            image.onload = function () {
                /*
                 * You have to manually tell the Promise that you are
                 * done dealing with asynchronous stuff and you are ready
                 * for it to give anything that attached a callback
                 * through .then a realized value.  We do that by calling
                 * resolve and passing it the realized value
                 */
                resolve(image)
            }
            /*
             * Setting the Image.src is what starts the networking process
             * to populate an image.  After you set it, the browser fires
             * a request to get the resource.  We attached a load listener
             * which will be called once the request finishes and we have
             * image data
             */
            image.src = url
        })
    }

    /**
     * Convert an image to ImageData, optionally resizing and using nearest-neighbor.
     *
     * Purpose:
     * - Draws `img` onto an offscreen canvas at (targetW × targetH) and returns the pixels.
     * - When `nearest` is true, disables canvas smoothing so discrete labels (e.g. mask indices)
     *   are preserved (no interpolation), mirroring GD’s IMG_NEAREST_NEIGHBOUR behavior.
     *
     * Notes:
     * - `willReadFrequently: true` hints to the browser we’ll read pixels, avoiding extra overhead.
     * - If `targetW/targetH` are null, the source’s natural size is used.
     *
     * @param {HTMLImageElement|CanvasImageSource} img      Source image
     * @param {?number} [targetW=null]                      Destination width (defaults to img.naturalWidth)
     * @param {?number} [targetH=null]                      Destination height (defaults to img.naturalHeight)
     * @param {boolean} [nearest=false]                     If true, force nearest-neighbor resampling
     * @returns {ImageData}                                 The RGBA pixel buffer at the requested size
     */
    static imageToImageData(img, targetW = null, targetH = null, nearest = false) {
        const w = targetW ?? img.naturalWidth
        const h = targetH ?? img.naturalHeight

        const c = document.createElement('canvas')
        c.width = w
        c.height = h
        const ctx = c.getContext('2d', { willReadFrequently: true })

        if (nearest) {
            // force nearest-neighbor so mask indices aren’t interpolated
            ctx.imageSmoothingEnabled = false
            ctx.mozImageSmoothingEnabled = false
            ctx.webkitImageSmoothingEnabled = false
            ctx.msImageSmoothingEnabled = false
        }

        ctx.drawImage(img, 0, 0, w, h)
        return ctx.getImageData(0, 0, w, h)
    }

    static base64ToArrayBuffer(base64) {
        const binary_string = window.atob(base64)
        const len = binary_string.length
        let bytes = []

        const byteNumbers = new Array(binary_string.length)

        for (let i = 0; i < len; i++) {
            byteNumbers[i] = binary_string.charCodeAt(i)
            // bytes[i] = binary_string.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers)
        bytes.push(byteArray)

        console.log(bytes)
        console.log(byteNumbers)
        console.log(byteArray)
        return bytes
    }

    /**
     * Hide all slider panels in the toolbar.
     * @returns {void}
     */
    static hideAllSlider() {
        DOM.hide(Selector.sliderLuminosityPanel)
        DOM.hide(Selector.sliderWheelAlt)
        DOM.hide(Selector.sliderScalePanel)
        DOM.hide(Selector.sliderRotatePanel)
        DOM.hide(Selector.brushSizeSlider)
    }

    /**
     * Generate a UUID-like value.
     * @returns {string}
     */
    static genUuid() {
        function rand(min, max) {
            return Math.floor(Math.random() * (max - min + 1)) + min
        }

        return (
            ('0000' + rand(0, 0xffff).toString(16)).slice(-4) +
            ('0000' + rand(0, 0xffff).toString(16)).slice(-4) +
            '-' +
            ('0000' + rand(0, 0xffff).toString(16)).slice(-4) +
            '-' +
            ('0000' + (rand(0, 0x0fff) | 0x4000).toString(16)).slice(-4) +
            '-' +
            ('0000' + (rand(0, 0x3fff) | 0x8000).toString(16)).slice(-4) +
            '-' +
            ('0000' + rand(0, 0xffff).toString(16)).slice(-4) +
            ('0000' + rand(0, 0xffff).toString(16)).slice(-4) +
            ('0000' + rand(0, 0xffff).toString(16)).slice(-4)
        )
    }

    /**
     * Populate the project form using stored data.
     * @param {Object} xml
     * @param {string} user_country_code
     * @returns {void}
     */
    static populateProjectInfoForm(xml, user_country_code) {
        console.log('populateProjectInfoForm: ' + user_country_code.toUpperCase())

        DOM.get(Selector.nameInput).value = xml.project.name ? xml.project.name : ''
        DOM.get(Selector.firstnameInput).value = xml.project.firstname ? xml.project.firstname : ''
        DOM.get(Selector.lastnameInput).value = xml.project.lastname ? xml.project.lastname : ''
        DOM.get(Selector.addressInput).value = xml.project.locationStreet1 ? xml.project.locationStreet1 : ''
        DOM.get(Selector.cityInput).value = xml.project.locationCity ? xml.project.locationCity : ''
        DOM.get(Selector.stateInput).value = xml.project.locationState ? xml.project.locationState : ''
        DOM.get(Selector.npaInput).value = xml.project.locationCP ? xml.project.locationCP : ''

        // display project country code first, if none, then use user_country_code
        const iso = (xml?.project?.locationCountry ?? user_country_code ?? '').toUpperCase()
        DOM.get(Selector.countryIDInput).value =
            document.querySelector(`${Selector.countryIDInput} option[data-iso="${iso}" i]`)?.value ?? ''
        DOM.get(Selector.notesInput).value = xml.project.notes ? xml.project.notes : ''
    }

    /**
     * Sync slider inputs with the currently selected color.
     * @param {ColorSample} colorSample
     * @returns {void}
     */
    static setSlidersValues(colorSample) {
        DOM.get(Selector.sliderRotate).value = colorSample.getCurrentColor().texture_rotation
        DOM.get(Selector.sliderScale).value = colorSample.getCurrentColor().texture_scale
        DOM.get(Selector.sliderOpacity).value = colorSample.getCurrentColor().bleach_value
    }

    /**
     * Truncate a string and append ellipsis.
     * @param {string} str
     * @param {number} num
     * @returns {string}
     */
    static truncateString(str, num) {
        // If the length of str is less than or equal to num
        // just return str--don't truncate it.
        if (str.length <= num) {
            return str
        }
        // Return str truncated with '...' concatenated to the end of str.
        return str.slice(0, num) + '...'
    }

    /**
     * Return the filename segment of a path.
     * @param {string} path
     * @returns {string}
     */
    static basename(path) {
        return path.split('/').reverse()[0]
    }

    /**
     * Read project fields from the DOM and build the nested payload you want.
     * Matches:
     * {
     *   name, firstname, lastname, notes,
     *   address: { street1, street2, npa, city, state, iso_3166_2 }
     * }
     */
    static getProjectFormData() {
        const byId = (id) => document.getElementById(id)
        const val = (el) => (el ? String(el.value).trim() : '')
        const orNull = (s) => (s === '' ? null : s)

        // Locate the country <select> (your component <x-forms.country-select ...>)
        // Adjusted: look for #country_id instead of #iso_3166_2
        const countryEl = document.querySelector(Selector.countryIDInput) || document.querySelector('[name="country_id"]')

        // Extract the ISO from the selected <option>
        let isoCode = ''
        if (countryEl && countryEl.selectedOptions.length > 0) {
            const selected = countryEl.selectedOptions[0]
            isoCode = selected.dataset.iso || ''
        }

        const payload = {
            name: orNull(val(byId('name'))),
            firstname: orNull(val(byId('firstname'))),
            lastname: orNull(val(byId('lastname'))),
            notes: orNull(val(byId('notes'))),
            address: {
                street1: orNull(val(byId('address'))), // single "address" input => street1
                street2: null, // not present in the form today
                npa: orNull(val(byId('npa'))), // postal code
                city: orNull(val(byId('city'))),
                state: orNull(val(byId('state'))),
                iso_3166_2: orNull(isoCode),
            },
        }

        return payload
    }

    /**
     * Map colors by their sample position.
     * @param {Array<Color>} colors
     * @returns {Array<Color>} Indexed by sample_position
     */
    static mapColorsByColorSamplePosition(colors) {
        const colorArray = new Array(5)
        colors.forEach((color) => {
            colorArray[color.sample_position] = color
        })
        return colorArray
    }
}
export default Utils
